class PrimerryPdpReviews extends HTMLElement {
  connectedCallback() {
    window.clearTimeout(this.destroyTimer);
    if (this.dataset.ready === 'true') return;

    this.dataset.ready = 'true';
    this.tabs = Array.from(this.querySelectorAll('[data-pdp-review-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-pdp-review-panel]'));
    this.swipers = new Map();
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);

    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
    this.initSwipers();
  }

  disconnectedCallback() {
    this.destroyTimer = window.setTimeout(() => {
      if (this.isConnected) return;
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.swipers.forEach((swiper) => swiper.destroy(true, true));
      this.swipers.clear();
      this.dataset.ready = 'false';
    });
  }

  async initSwipers() {
    try {
      const SwiperClass = await this.ensureSwiperAssets();
      if (!this.isConnected) return;

      this.querySelectorAll('[data-pdp-review-swiper]').forEach((element) => {
        if (this.swipers.has(element)) return;

        const originalCount = Number(element.dataset.reviewCount) || 0;
        // The PDP review rail, [data-pdp-review-swiper]: no cloned slides and no
        // wrap-around, per Kanak 16 Sep 2026. A tab whose cards do not fill the
        // row (2 to 4 on desktop) sits centred, per Unnati 5 Oct 2026.
        const swiper = new SwiperClass(element, {
          slidesPerView: 'auto',
          centeredSlides: false,
          centeredSlidesBounds: false,
          centerInsufficientSlides: true,
          initialSlide: Number(element.dataset.initialSlide) || 0,
          loop: false,
          spaceBetween: 8,
          speed: 650,
          grabCursor: true,
          simulateTouch: true,
          allowTouchMove: true,
          threshold: 4,
          touchRatio: 1,
          resistance: true,
          resistanceRatio: 0.72,
          longSwipesRatio: 0.15,
          longSwipesMs: 250,
          watchOverflow: true,
          freeMode: {
            enabled: true,
            momentum: true,
            momentumBounce: false,
            momentumRatio: 0.82,
            momentumVelocityRatio: 0.8,
            minimumVelocity: 0.02,
          },
          mousewheel: {
            enabled: true,
            forceToAxis: true,
            releaseOnEdges: false,
            sensitivity: 0.65,
            thresholdDelta: 2,
          },
          breakpoints: {
            750: { spaceBetween: 12 },
          },
          observer: true,
          observeParents: true,
          resizeObserver: true,
          watchSlidesProgress: true,
          updateOnWindowResize: true,
        });

        this.swipers.set(element, swiper);
        requestAnimationFrame(() => {
          if (swiper.destroyed) return;
          swiper.update();
        });
      });
    } catch (error) {
      this.dataset.swiperFallback = 'true';
    }
  }

  addLoopSlides(element, originalCount) {
    const wrapper = element.querySelector('.swiper-wrapper');
    if (!wrapper || originalCount < 2 || wrapper.dataset.loopCopies === 'true') return;

    const originals = Array.from(wrapper.children).slice(0, originalCount);
    const minimumSlideCount = 25;
    let cloneIndex = 0;

    while (wrapper.children.length < minimumSlideCount) {
      const clone = originals[cloneIndex % originals.length].cloneNode(true);
      clone.dataset.reviewClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      wrapper.appendChild(clone);
      cloneIndex += 1;
    }

    wrapper.dataset.loopCopies = 'true';
  }

  async ensureSwiperAssets() {
    const stylesheetPromise = new Promise((resolve, reject) => {
      let stylesheet = document.querySelector('link[href*="swiper-bundle.min.css"]');

      if (stylesheet?.sheet) {
        resolve();
        return;
      }

      if (!stylesheet) {
        stylesheet = document.createElement('link');
        stylesheet.rel = 'stylesheet';
        stylesheet.href = 'https://cdn.jsdelivr.net/npm/swiper@14.2.0/swiper-bundle.min.css';
        stylesheet.dataset.primerrySwiperStyle = 'true';
        document.head.appendChild(stylesheet);
      }

      stylesheet.addEventListener('load', resolve, { once: true });
      stylesheet.addEventListener('error', () => reject(new Error('Swiper stylesheet failed to load.')), { once: true });
    });

    const scriptPromise = new Promise((resolve, reject) => {
      if (window.Swiper) {
        resolve(window.Swiper);
        return;
      }

      let script = document.querySelector('script[src*="swiper-bundle.min.js"]');

      if (!script) {
        script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/swiper@14.2.0/swiper-bundle.min.js';
        script.defer = true;
        script.dataset.primerrySwiperScript = 'true';
        document.head.appendChild(script);
      }

      script.addEventListener('load', () => resolve(window.Swiper), { once: true });
      script.addEventListener('error', () => reject(new Error('Swiper script failed to load.')), { once: true });
    });

    const [, SwiperClass] = await Promise.all([stylesheetPromise, scriptPromise]);
    if (!SwiperClass) throw new Error('Swiper failed to initialize.');
    return SwiperClass;
  }

  handleClick(event) {
    const tab = event.target.closest('[data-pdp-review-tab]');
    if (!tab || !this.contains(tab)) return;
    this.activateTab(tab.dataset.pdpReviewTab, true);
  }

  handleKeydown(event) {
    const current = event.target.closest('[data-pdp-review-tab]');
    if (!current || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();
    let index = this.tabs.indexOf(current);
    if (event.key === 'Home') index = 0;
    if (event.key === 'End') index = this.tabs.length - 1;
    if (event.key === 'ArrowRight') index = (index + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft') index = (index - 1 + this.tabs.length) % this.tabs.length;

    const next = this.tabs[index];
    this.activateTab(next.dataset.pdpReviewTab, true);
    next.focus({ preventScroll: true });
  }

  activateTab(handle, scrollTab) {
    this.tabs.forEach((tab) => {
      const active = tab.dataset.pdpReviewTab === handle;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
      if (active && scrollTab) {
        tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    });

    this.panels.forEach((panel) => {
      const active = panel.dataset.pdpReviewPanel === handle;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
      if (!active) return;

      requestAnimationFrame(() => {
        const element = panel.querySelector('[data-pdp-review-swiper]');
        const swiper = this.swipers.get(element);
        if (!swiper) return;
        swiper.update();
        const initialSlide = Number(element.dataset.initialSlide) || 0;
        if (swiper.params.loop) {
          swiper.slideToLoop(initialSlide, 0, false);
        } else {
          swiper.slideTo(initialSlide, 0, false);
        }
      });
    });
  }
}

if (!customElements.get('primerry-pdp-reviews')) {
  customElements.define('primerry-pdp-reviews', PrimerryPdpReviews);
}
